'use client';
import { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Grip, ChevronRight, Check, ChevronLeft, Languages, X, TabletSmartphone, Palette, Sun, Moon, LaptopMinimal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GlobalContext } from '@/contexts/global-context';
import { useIsMobile } from '@/hooks/use-mobile';
import { Modal, QRCode } from 'antd';
import { getSidebarConfig, type SideBarMenuItemInput } from '@/app/const/sidebar';
import { useIsLoggedIn, useUserInfo } from '@/app/stores/useUserStore';
import { Badge } from '@/components/ui/badge';
import AboutDialog from '@/app/article/about/page';
import { useSupport } from '@/hooks/use-support';
import { cn } from '@/lib/utils';
import { DOWNLOAD_BUTTONS } from '@/app/const';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip as AntdTooltip } from 'antd';

export default function CoSetting() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isLoggedIn = useIsLoggedIn();
  const userInfo = useUserInfo();
  const { lang, theme, setTheme, setLang, themeColor, setThemeColor } = useContext(GlobalContext);
  const supportQAUrl = useSupport('qA');
  const downloadButtons = DOWNLOAD_BUTTONS as Array<(typeof DOWNLOAD_BUTTONS)[number] & { disabled?: boolean }>;
  const [langOpen, setLangOpen] = useState(false);
  const [downloadAppOpen, setDownloadAppOpen] = useState(false);
  const [settingOpen, setSettingOpen] = useState(false);
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const [isMobileLangDialogOpen, setIsMobileLangDialogOpen] = useState(false);
  const [isAboutDialogOpen, setIsAboutDialogOpen] = useState(false);
  // 获取侧边栏配置
  const sidebarConfig = getSidebarConfig(t, isLoggedIn, userInfo, isMobile);
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  // 语言列表
  const langList = [
    {
      value: 'zh-CN',
      label: '简体中文',
    },
    {
      value: 'zh-TW',
      label: '繁體中文',
    },
    {
      value: 'en-US',
      label: 'English',
    },
    {
      value: 'ja-JP',
      label: '日本語',
    },
    {
      value: 'ko-KR',
      label: '한국어',
    },
  ];
  const handleThemeChange = (newTheme: string) => {
    setTheme && setTheme(newTheme);
  };
  const handleThemeColorChange = (newThemeColor: string) => {
    setThemeColor && setThemeColor(newThemeColor);
  };
  const handleLangChange = (newLang: string) => {
    setLang && setLang(newLang);
    // 不自动关闭语言选择面板，让用户可以看到选择结果
    setLangOpen(false);
    // 移动端关闭 Dialog
    if (isMobile) {
      setIsMobileLangDialogOpen(false);
    }
  };

  // 处理侧边栏菜单项点击
  const handleSidebarItemClick = (item: SideBarMenuItemInput) => {
    // 跳转tab
    let newTab: string = item.path || '';
    // 直播管理
    if (newTab === 'admin') {
      navigate('/livecenter/shop');
      setIsMobilePanelOpen(false);
      return;
    }
    // 直播间内部跳转
    navigate(`/${newTab}`);
    setIsMobilePanelOpen(false);
  };

  // 渲染侧边栏导航菜单
  const renderSidebarNavigation = () => {
    return (
      <div className="flex flex-col gap-1">
        {sidebarConfig.map((group, groupIndex) => (
          <div key={groupIndex} className="flex flex-col gap-1">
            {group.map(item => {
              if (item.hidden || item.isSoon) return null;
              // 判断当前路径是否匹配
              const currentPath = pathname.replace('/', '') || '';
              const isActive = item.paths?.some(
                p => currentPath === p || currentPath.startsWith(p + '/') || pathname === `/${p}` || pathname.startsWith(`/${p}/`)
              );
              return (
                <div
                  key={item.path}
                  className={cn(
                    'flex items-center gap-3 py-1.5 px-2 rounded-lg cursor-pointer transition-colors',
                    isActive ? 'bg-background-3/15 text-font-primary' : 'hover:bg-secondary-hover-3/10 text-font-secondary'
                  )}
                  onClick={() => handleSidebarItemClick(item)}
                >
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    {isActive && item.activeIcon ? (
                      <item.activeIcon className={item.size || 'h-5 w-5 flex-none'} />
                    ) : (
                      <item.icon className={item.size || 'h-5 w-5 flex-none'} fill={isActive ? 'currentColor' : 'none'} />
                    )}
                  </div>
                  <span className="text-sm flex-1">{item.text}</span>
                  {item.isNew && (
                    <Badge variant="danger-gradient" size="xs" className="rounded-full">
                      {item.isSoon ? 'Soon' : 'New'}
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        <div className="w-full h-[1px] m-1 bg-button-border/75" />
      </div>
    );
  };
  const renderLangContent = () => {
    return (
      <div className={`w-[200px] flex flex-col gap-1 px-2 ${langOpen ? 'block' : 'hidden'}`}>
        <div className="py-2 flex items-center gap-1 text-sm text-font-primary cursor-pointer" onClick={() => setLangOpen(false)}>
          <ChevronLeft className="w-3 h-3" />
          {t('coSetting.back')}
        </div>
        {langList.map(item => (
          <DropdownMenuItem
            key={item.value}
            className={`px-8 ${lang === item.value && 'bg-accent text-accent-foreground'}`}
            onSelect={e => {
              e.preventDefault();
              handleLangChange(item.value);
            }}
          >
            {lang === item.value && <Check className="w-4 h-4 absolute left-2" />}
            <span>{item.label}</span>
          </DropdownMenuItem>
        ))}
      </div>
    );
  };
  const renderDownloadAppContent = () => {
    return (
      <div className={`w-[240px] flex flex-col gap-3 px-3 py-2 rounded-lg ${downloadAppOpen ? 'block' : 'hidden'}`}>
        <div
          className="py-1 flex items-center gap-1 text-sm text-font-primary cursor-pointer hover:opacity-80"
          onClick={() => setDownloadAppOpen(false)}
        >
          <ChevronLeft className="w-3 h-3 flex-shrink-0" />
          {t('coSetting.back')}
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="rounded-lg">
            <QRCode errorLevel="H" value="https://billion.live/app-download" icon="https://billion.live/favicon.svg" />
          </div>
          <span className="text-xs text-font-primary text-center">{t('coSetting.scanDownloadApp')}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {downloadButtons.map(item => {
            const Icon = item.icon as any;
            const labelKey = item.labelKey as string;
            return (
              <div
                key={item.key}
                className={cn(
                  'flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-font-primary text-xs cursor-pointer transition-colors bg-background-1/50 relative',
                  item.fullRow && 'col-span-2',
                  'disabled' in item && item.disabled ? 'cursor-not-allowed bg-background-1/25' : 'hover:bg-background-1/70'
                )}
                onClick={() => {
                  if ('disabled' in item && item.disabled) return;
                  if (item.href.startsWith('http')) {
                    window.open(item.href, '_blank');
                  } else {
                    navigate(item.href);
                  }
                }}
              >
                {'disabled' in item && item.disabled && (
                  <div className="absolute top-0 right-0 text-xxs text-black bg-primary rounded-bl-lg rounded-tr-lg px-1">
                    {t('coSetting.comingSoon')}
                  </div>
                )}
                <div className={cn('flex items-center gap-2', 'disabled' in item && item.disabled ? 'opacity-50' : 'opacity-100')}>
                  <Icon className="size-3.5 flex-shrink-0" />
                  <span className="text-xxs">{t(`coSetting.${labelKey}`)}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div
          className="flex items-center gap-1 text-sm text-font-primary cursor-pointer hover:opacity-80 py-1"
          onClick={() => {
            setDownloadAppOpen(false);
            navigate('/app-download/');
          }}
        >
          {t('coSetting.learnMore')}
          <ChevronRight className="w-3 h-3 flex-shrink-0" />
        </div>
      </div>
    );
  };
  const renderSettingContent = () => {
    return (
      <div className={`w-full px-0 md:w-[240px] md:px-2 flex flex-col py-2 gap-1 ${langOpen || downloadAppOpen ? 'hidden' : 'block'}`}>
        <div className="p-2 flex items-center justify-between hover:bg-transparent">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <Palette className="w-4 h-4" />
            </div>
            <span className="text-sm text-font-primary">{t('coSetting.theme')}</span>
          </div>
          <ToggleGroup
            type="single"
            className="bg-background dark:bg-secondary/60 gap-0.5"
            variant="secondary"
            value={theme}
            onValueChange={value => {
              if (value === '') return;
              handleThemeChange(value);
            }}
          >
            <AntdTooltip title={t('coSetting.light')}>
              <ToggleGroupItem value="light" className="h-6 w-6 px-0 min-w-6">
                <Sun className="w-4 h-4" fill="currentColor" />
              </ToggleGroupItem>
            </AntdTooltip>
            <AntdTooltip title={t('coSetting.dark')}>
              <ToggleGroupItem value="dark" className="h-6 w-6 px-0 min-w-6">
                <Moon className="w-4 h-4" fill="currentColor" />
              </ToggleGroupItem>
            </AntdTooltip>
            <AntdTooltip title={t('coSetting.device')}>
              <ToggleGroupItem value="system" className="h-6 w-6 px-0 min-w-6">
                <LaptopMinimal className="w-4 h-4" fill="currentColor" />
              </ToggleGroupItem>
            </AntdTooltip>
          </ToggleGroup>
        </div>
        {/* {
                    (theme === 'dark' || (theme === 'system' && prefersDark)) && (
                        <div className='p-2 flex items-center justify-between hover:bg-transparent'>
                            // <div className='flex items-center gap-1'>
                                // <PaintBucket className='w-4 h-4' />
                                <span className='text-sm text-font-primary'>{t('coSetting.themeColor')}</span>
                            </div>
                            <ToggleGroup type="single" className='bg-[rgba(22,24,35,0.4)] p-1 gap-1' value={themeColor} onValueChange={(value) => {
                                if (value === '') return;
                                handleThemeColorChange(value)
                            }}>
                                <AntdTooltip title={t('coSetting.greenTheme')}>
                                    <ToggleGroupItem value="dark-green" className='h-6 w-6 px-0 min-w-6 bg-green-500/30 hover:bg-green-500/40 hover:text-font-primary data-[state=on]:bg-green-500/40 border border-white/30'>
                                        {themeColor === 'dark-green' && <Check className='w-4 h-4' />}
                                    </ToggleGroupItem>
                                </AntdTooltip>
                                <AntdTooltip title={t('coSetting.blackTheme')}>
                                    <ToggleGroupItem value="dark-black" className='h-6 w-6 px-0 min-w-6 bg-black hover:bg-black hover:text-font-primary data-[state=on]:bg-black border border-white/30'>
                                        {themeColor === 'dark-black' && <Check className='w-4 h-4' />}
                                    </ToggleGroupItem>
                                </AntdTooltip>
                                <AntdTooltip title={t('coSetting.greenTheme')}>
                                    <ToggleGroupItem value="dark-orange" className='h-6 w-6 px-0 min-w-6 bg-orange-500/30 hover:bg-orange-500/40 hover:text-font-primary data-[state=on]:bg-orange-500/40 border border-white/30'>
                                        {themeColor === 'dark-orange' && <Check className='w-4 h-4' />}
                                    </ToggleGroupItem>
                                </AntdTooltip>
                            </ToggleGroup>
                        </div>
                    )
                } */}
        <div
          className="p-2 flex items-center justify-between hover:bg-secondary-hover rounded-md cursor-pointer"
          onClick={() => {
            if (isMobile) {
              setIsMobileLangDialogOpen(true);
            } else {
              setLangOpen(true);
            }
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <Languages className="w-4 h-4" />
            </div>
            <span className="text-sm text-font-primary">{t('coSetting.language')}</span>
          </div>
          <span className="text-xs text-font-secondary flex items-center gap-1">
            {langList.find(item => item.value === lang)?.label || lang}
            <ChevronRight className="w-3 h-3" />
          </span>
        </div>
        <div
          className="p-2 flex items-center justify-between hover:bg-secondary-hover rounded-md cursor-pointer"
          onClick={() => {
            if (isMobile) {
              navigate('/app-download');
            } else {
              setDownloadAppOpen(true);
            }
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <TabletSmartphone className="w-4 h-4" />
            </div>
            <span className="text-sm text-font-primary">{t('header.downloadApp')}</span>
          </div>
          <span className="text-xs text-font-secondary flex items-center gap-1">
            <ChevronRight className="w-3 h-3" />
          </span>
        </div>
        <div className="w-full h-[1px] m-1 bg-button-border/75" />
        <div
          className="p-2 flex items-center justify-between hover:bg-secondary-hover rounded-md cursor-pointer"
          onClick={() => {
            window.open(supportQAUrl, '_blank');
          }}
        >
          <div className="flex items-center gap-3">
            <span className="text-sm text-font-primary">{t('footer.support')}</span>
          </div>
          <span className="text-xs text-font-secondary flex items-center gap-1">
            <ChevronRight className="w-3 h-3" />
          </span>
        </div>
        <div
          className="p-2 flex items-center justify-between hover:bg-secondary-hover rounded-md cursor-pointer"
          onClick={() => setIsAboutDialogOpen(true)}
        >
          <span className="text-sm text-font-primary">{t('coSetting.aboutUs')}</span>
        </div>
        <AboutDialog open={isAboutDialogOpen} onOpenChange={setIsAboutDialogOpen} />
      </div>
    );
  };

  if (isMobile) {
    return (
      <>
        <div
          className="transition-all co-link flex items-center cursor-pointer p-2 gap-1 rounded-md bg-background-card border border-button-border/50 dark:border-button-border/40 hover:border-button-border/70"
          onClick={() => setIsMobilePanelOpen(true)}
        >
          <Grip className="w-4 h-4" />
        </div>
        {/* 移动端全屏设置面板 */}
        {isMobilePanelOpen && (
          <>
            {/* 背景遮罩 */}
            {/* 设置面板 */}
            <div
              className={cn(
                'fixed inset-0 z-[100000] bg-background-card w-dvw h-dvh transform transition-transform duration-300 ease-out flex flex-col',
                isMobilePanelOpen ? 'translate-x-0' : 'translate-x-full'
              )}
            >
              {/* 顶部栏：左侧logo，右侧关闭按钮 */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-button-border/50 dark:border-button-border/40 flex-shrink-0">
                {/* 左侧logo */}
                <div className="co-logo w-20" />
                {/* 右侧关闭按钮 */}
                <div onClick={() => setIsMobilePanelOpen(false)} className="p-2 rounded-md hover:bg-secondary-hover-card transition-colors">
                  <X className="w-5 h-5 text-font-primary" />
                </div>
              </div>
              {/* 设置内容区域 */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                {renderSidebarNavigation()}
                {renderSettingContent()}
              </div>
            </div>
          </>
        )}
        <Modal
          open={isMobileLangDialogOpen}
          onCancel={() => setIsMobileLangDialogOpen(false)}
          footer={null}
          title={t('coSetting.language')}
          width="90vw"
          centered
        >
          <div className="mt-4 flex flex-col gap-2 px-1">
            {langList.map(item => (
              <div
                key={item.value}
                className={cn(
                  'flex items-center justify-between rounded-md p-3 transition-colors',
                  lang === item.value ? 'bg-accent text-accent-foreground' : 'hover:bg-secondary-hover-card'
                )}
                onClick={() => handleLangChange(item.value)}
              >
                <span className="text-sm">{item.label}</span>
                {lang === item.value && <Check className="w-4 h-4" />}
              </div>
            ))}
          </div>
        </Modal>
      </>
    );
  }
  return (
    <DropdownMenu open={settingOpen} onOpenChange={setSettingOpen}>
      <DropdownMenuTrigger asChild>
        <div className="transition-all flex items-center cursor-pointer gap-1 rounded-md bg-background-card border border-button-border/85 relative p-2 hover:border-button-border text-font-secondary hover:text-font-primary hover:bg-secondary-hover">
          <Grip className="w-4 h-4" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="bottom" sideOffset={10}>
        {renderSettingContent()}
        {renderLangContent()}
        {renderDownloadAppContent()}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
